import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Chip, Icon, Swatch } from "@/components/primitives";
import { MobileFrame } from "@/components/MobileFrame";
import { useGrails, useGrailMatches } from "@/hooks/useFigData";
import { ensureConversation } from "@/hooks/useMessages";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/lib/toast";
import { GrailForm } from "@/components/GrailForm";
import type { Grail } from "@/hooks/useFigData";

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function grailSummary(g: Grail): string {
  const parts: string[] = [];
  const filled = (v: string | null | undefined) => v && v !== "Any";
  const filledArr = (a: string[] | null | undefined) =>
    Array.isArray(a) ? a.filter((x) => x && x !== "Any") : [];
  const cat = g.category;
  if (cat === "Clothing") {
    if (filled(g.size_value)) parts.push(`Size ${g.size_value}`);
    else if (filled(g.size)) parts.push(`Size ${g.size}`);
    if (filled(g.length_preference)) parts.push(g.length_preference!);
  } else if (cat === "Shoes") {
    if (filled(g.size_value)) parts.push(`${g.size_system ?? "US"} ${g.size_value}`);
    const styles = filledArr(g.style_preference);
    if (styles.length) parts.push(styles[0]);
  } else if (cat === "Bags") {
    if (filled(g.size_value)) parts.push(g.size_value!);
    const mats = filledArr(g.material_preference);
    if (mats.length) parts.push(mats[0]);
    if (filled(g.hardware_preference)) parts.push(g.hardware_preference!);
  } else if (cat === "Accessories") {
    const types = filledArr(g.accessory_type);
    if (types.length) parts.push(types[0]);
    if (filled(g.hat_size)) parts.push(g.hat_size!);
    if (filled(g.frame_size)) parts.push(g.frame_size!);
    if (filled(g.waist_inches)) parts.push(`${g.waist_inches}"`);
  } else if (cat === "Jewellery") {
    const types = filledArr(g.jewellery_type);
    if (types.length) parts.push(types[0]);
    if (filled(g.ring_size)) parts.push(`Size ${g.ring_size}`);
    if (filled(g.necklace_length)) parts.push(g.necklace_length!);
    if (filled(g.earring_style)) parts.push(g.earring_style!);
    if (filled(g.wrist_size)) parts.push(g.wrist_size!);
    if (filled(g.metal_preference)) parts.push(g.metal_preference!);
  } else if (filled(g.size)) {
    parts.push(`size ${g.size}`);
  }
  const eras = filledArr(g.era_preference);
  if (eras.length) parts.push(eras[0]);
  if (filled(g.condition_preference)) parts.push(g.condition_preference!);
  return parts.join(" · ");
}

export default function Grails() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { grails, loading, add, remove } = useGrails();
  const { matches, loading: matchesLoading, dismiss, markAllSeen, refresh: refreshMatches } = useGrailMatches();
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [openingOffer, setOpeningOffer] = useState<string | null>(null);

  const runBackfill = async () => {
    if (backfilling) return;
    setBackfilling(true);
    toast.message("Backfilling semantic matches…", { description: "This can take ~30s. FIG is generating canonical descriptions for every grail and posted scan." });
    try {
      const { data, error } = await supabase.functions.invoke("backfill-canonical-matches", { body: {} });
      if (error) throw error;
      const d = data as {
        grails_total: number; scans_total: number;
        grails_canonicalized: number; scans_canonicalized: number;
        matches_inserted: number; matches_upgraded: number;
        ai_errors: string[];
      };
      toast.success("Backfill complete", {
        description: `${d.grails_canonicalized}/${d.grails_total} grails + ${d.scans_canonicalized}/${d.scans_total} scans canonicalized · ${d.matches_inserted} new matches, ${d.matches_upgraded} upgraded`,
      });
      await refreshMatches();
    } catch (e) {
      toast.error("Backfill failed", { description: e instanceof Error ? e.message : "Unknown error" });
    } finally {
      setBackfilling(false);
    }
  };

  // Mark visible matches as seen after they've been on screen briefly
  useEffect(() => {
    if (matches.length === 0) return;
    const t = setTimeout(() => { markAllSeen(); }, 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matches.length]);

  // Auto-scroll to the matches section when the user lands here from a
  // notification, dock badge, or Home callout (?focus=matches). Wait one
  // tick for the section to render after `matches` resolves.
  useEffect(() => {
    if (searchParams.get("focus") !== "matches") return;
    if (matchesLoading) return;
    const t = setTimeout(() => {
      document
        .getElementById("grail-matches")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
    return () => clearTimeout(t);
  }, [searchParams, matchesLoading, matches.length]);

  const submitGrail = async (payload: Record<string, unknown>) => {
    setBusy(true);
    try {
      await add(payload as never);
      setShowForm(false);
    } finally {
      setBusy(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: "100%", padding: "10px 12px",
    border: "1px solid hsl(var(--ink))", background: "hsl(var(--paper))",
    fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, color: "hsl(var(--ink))", outline: "none",
  };

  return (
    <MobileFrame>
      <div className="paper-bg" style={{ minHeight: "100vh", paddingBottom: 40 }}>
        <div style={{ padding: "12px 20px 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <button onClick={() => nav("/home")} aria-label="Back to home" style={{ background: "none", border: "none", padding: 6, cursor: "pointer", color: "hsl(var(--ink))" }}>{Icon.chev(18, "l")}</button>
          <span className="caps">Want list</span>
          <div style={{ width: 30 }} />
        </div>
        <div style={{ padding: "10px 20px 0" }}>
          <span className="caps" style={{ color: "hsl(var(--rust))" }}>Looking for</span>
          <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 900, fontSize: 48, color: "hsl(var(--ink))", letterSpacing: "-0.03em", lineHeight: 0.9, marginTop: 4 }}>
            Your Grails
          </div>
          <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 14, color: "hsl(var(--ink-2))", marginTop: 4 }}>
            we'll ping you when a matching scan hits The Archive
          </div>
        </div>

        {(<>

        {/* Live grail matches — real data, sits above the want-list */}
        {!matchesLoading && matches.length > 0 && (
          <div id="grail-matches" style={{ padding: "18px 20px 0", display: "flex", flexDirection: "column", gap: 10, scrollMarginTop: 60 }}>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
              <span className="caps" style={{ color: "hsl(var(--marigold))" }}>
                Hot matches · {matches.length}
              </span>
              <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, letterSpacing: "0.08em", color: "hsl(var(--ink-2))" }}>
                LIVE
              </span>
            </div>
            {matches.map((m) => (
              <div key={m.id} style={{ position: "relative" }}>
                <div style={{ background: "hsl(var(--ink))", color: "hsl(var(--ink-fg))", padding: "12px 14px", position: "relative" }}>
                  <button
                    onClick={() => dismiss(m.id)}
                    aria-label="Dismiss"
                    style={{
                      position: "absolute", top: 6, right: 8, background: "none", border: "none",
                      cursor: "pointer", color: "hsl(var(--cream))", opacity: 0.6,
                      fontFamily: "'IBM Plex Mono', monospace", fontSize: 16, padding: 4,
                    }}
                  >×</button>

                  <div className="caps" style={{ color: "hsl(var(--marigold))", fontSize: 9 }}>
                    matches "{m.grail_item}" · {timeAgo(m.created_at)}
                  </div>

                  <div style={{ display: "flex", gap: 10, marginTop: 8, alignItems: "stretch" }}>
                    {m.scan_photo ? (
                      <img
                        src={m.scan_photo}
                        alt={m.scan_title}
                        style={{ width: 64, height: 64, objectFit: "cover", border: "1px solid hsl(var(--cream))", flexShrink: 0 }}
                      />
                    ) : (
                      <Swatch kind={m.scan_img as never} style={{ width: 64, height: 64, border: "1px solid hsl(var(--cream))", flexShrink: 0 }} />
                    )}

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 800, fontSize: 15, lineHeight: 1.15 }}>
                        {m.scan_title}
                      </div>
                      <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: "hsl(var(--cream))", opacity: 0.7, marginTop: 3, letterSpacing: "0.06em" }}>
                        {m.scan_brand ? m.scan_brand : ""}{m.scan_brand && m.scan_era ? " · " : ""}{m.scan_era ?? ""}
                      </div>
                      <div style={{ display: "flex", gap: 6, marginTop: 6, alignItems: "center", flexWrap: "wrap" }}>
                        {m.matched_price != null && (
                          <span style={{
                            fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 800, fontSize: 16,
                            color: m.over_budget ? "hsl(var(--marigold))" : "hsl(var(--cream))",
                          }}>
                            ${Number(m.matched_price).toFixed(0)}
                          </span>
                        )}
                        {m.over_budget && (
                          <Chip filled style={{ background: "transparent", borderColor: "hsl(var(--marigold))", color: "hsl(var(--marigold))", fontSize: 8 }}>
                            OVER BUDGET
                          </Chip>
                        )}
                        <button
                          onClick={() => nav(`/profile/@${m.seller_handle}`)}
                          style={{
                            background: "none", border: "none", padding: 0, cursor: "pointer",
                            color: "hsl(var(--marigold))", fontFamily: "'IBM Plex Mono', monospace",
                            fontSize: 11, letterSpacing: "0.06em", textDecoration: "underline",
                          }}
                        >
                          @{m.seller_handle}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                    <button
                      onClick={() => nav(`/feed?scan=${m.scan_id}`)}
                      className="btn btn-rust"
                      style={{ padding: "8px 14px", fontSize: 12 }}
                    >
                      See listing →
                    </button>
                    <button
                      onClick={async () => {
                        if (!user) {
                          toast.error("Sign in to send a note.");
                          return;
                        }
                        if (openingOffer) return;
                        setOpeningOffer(m.id);
                        const convoId = await ensureConversation(user.id, m.seller_id, m.scan_id);
                        setOpeningOffer(null);
                        if (!convoId) {
                          toast.error("Couldn't open conversation");
                          return;
                        }
                        // Fig isn't a marketplace — DMs are collector-to-collector
                        // notes (questions, leads, "where did you find it?").
                        // Seed a friendly opener instead of an offer.
                        const draft = `Hey — saw this on your feed and it's on my grail list. Mind sharing where you're listing it?`;
                        nav(`/messages/${convoId}?draft=${encodeURIComponent(draft)}`);
                      }}
                      disabled={openingOffer === m.id}
                      className="btn btn-primary"
                      style={{ padding: "8px 14px", fontSize: 12, background: "hsl(var(--marigold))", color: "hsl(var(--marigold-fg))", borderColor: "hsl(var(--marigold))", opacity: openingOffer === m.id ? 0.6 : 1 }}
                    >
                      {openingOffer === m.id ? "…" : "Send a note"}
                    </button>
                    <button
                      onClick={() => dismiss(m.id)}
                      aria-label="Dismiss match"
                      className="btn"
                      style={{ padding: "8px 10px", fontSize: 12, background: "transparent", color: "hsl(var(--cream))", borderColor: "hsl(var(--cream))" }}
                    >
                      ×
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div style={{ padding: "18px 20px 0", display: "flex", flexDirection: "column", gap: 10 }}>
          {loading && (
            <div className="caps" style={{ color: "hsl(var(--ink-2))", padding: "20px 0", textAlign: "center" }}>Loading…</div>
          )}
          {!loading && grails.length === 0 && !showForm && (
            <div style={{ background: "hsl(var(--cream))", border: "1px dashed hsl(var(--ink))", padding: "20px 14px", textAlign: "center" }}>
              <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 15, color: "hsl(var(--ink-2))" }}>
                No grails yet. Add the piece you've been hunting for years.
              </div>
            </div>
          )}
          {grails.map((g, i) => (
            <div key={g.id} style={{ background: "hsl(var(--cream))", border: "1px solid hsl(var(--ink))", padding: "12px 14px", position: "relative" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                <span className="caps" style={{ color: "hsl(var(--rust))" }}>Wish № {i + 1}</span>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  {g.hits > 0 && (
                    <Chip filled style={{ background: "hsl(var(--rust))", borderColor: "hsl(var(--rust))" }}>
                      {g.hits} {g.hits === 1 ? "MATCH" : "MATCHES"}
                    </Chip>
                  )}
                  <button onClick={() => remove(g.id)} aria-label="Remove" style={{ background: "none", border: "none", cursor: "pointer", color: "hsl(var(--ink-2))", fontFamily: "'IBM Plex Mono', monospace", fontSize: 14 }}>×</button>
                </div>
              </div>
              <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 800, fontSize: 18, color: "hsl(var(--ink))", marginTop: 4, lineHeight: 1.15 }}>
                {g.item}
              </div>
              <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: "hsl(var(--ink-2))", marginTop: 4 }}>
                {grailSummary(g) || `size ${g.size || "any"}`}{g.max_price ? ` · up to $${g.max_price}` : ""}
              </div>
            </div>
          ))}

          {showForm ? (
            <GrailForm busy={busy} onCancel={() => setShowForm(false)} onSubmit={submitGrail} />
          ) : (
            <button onClick={() => setShowForm(true)} style={{
              background: "transparent", border: "1.5px dashed hsl(var(--ink))", padding: "14px",
              cursor: "pointer", fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
              letterSpacing: "0.12em", textTransform: "uppercase", color: "hsl(var(--ink))",
            }}>
              + Add a grail
            </button>
          )}

          {/* Admin: one-time semantic backfill across all historical grails + scans */}
          <button
            onClick={runBackfill}
            disabled={backfilling}
            style={{
              marginTop: 18, background: "transparent", border: "none", padding: "10px",
              cursor: backfilling ? "wait" : "pointer",
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
              letterSpacing: "0.14em", textTransform: "uppercase",
              color: "hsl(var(--ink-2))", opacity: backfilling ? 0.65 : 1,
              textDecoration: "underline", textUnderlineOffset: 4,
            }}
          >
            {backfilling ? "Backfilling…" : "⚙ Backfill semantic matches"}
          </button>
        </div>
        </>)}

      </div>
    </MobileFrame>
  );
}
